import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  Chip,
  CircularProgress
} from '@mui/material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

const ENTITY_TABS = [
  { key: 'contact', label: 'Contacts' },
  { key: 'account', label: 'Accounts' },
  { key: 'job_requirement', label: 'Job requirements' },
  { key: 'document', label: 'Documents' },
  { key: 'user', label: 'Users' },
  { key: 'employee', label: 'Employees' }
];

function rowLabel(entityType, row) {
  switch (entityType) {
    case 'contact':
      return `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.email;
    case 'account':
      return row.company_name || row.account_code || row.id;
    case 'job_requirement':
      return row.title || row.job_code || row.id;
    case 'document':
      return row.title || row.file_name || row.id;
    case 'user':
      return row.email || row.id;
    case 'employee':
      return row.employee_code || row.id;
    default:
      return row.id;
  }
}

export default function AdminArchivedData() {
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [exportTypes, setExportTypes] = useState({
    contact: true,
    account: true,
    job_requirement: false,
    document: false,
    user: false,
    employee: false
  });
  const [includeDocs, setIncludeDocs] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [permDelete, setPermDelete] = useState(null);

  const entityType = ENTITY_TABS[tab]?.key || 'contact';

  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get('/admin/data-archive/summary');
      setSummary(r.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load archive summary');
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/data-archive/list/${encodeURIComponent(entityType)}`, {
        params: { page: 1, limit: 100 }
      });
      setList(r.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load archived rows');
      setList(null);
    } finally {
      setLoading(false);
    }
  }, [entityType]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const restore = async (id) => {
    setBusyId(id);
    try {
      await api.post('/admin/data-archive/restore', { entity_type: entityType, entity_id: id });
      toast.success('Restored');
      await loadSummary();
      await loadList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Restore failed');
    } finally {
      setBusyId(null);
    }
  };

  const permanentDelete = async () => {
    if (!permDelete) return;
    setBusyId(permDelete.id);
    try {
      await api.delete(
        `/admin/data-archive/permanent/${encodeURIComponent(permDelete.entityType)}/${encodeURIComponent(permDelete.id)}`
      );
      toast.success('Permanently deleted');
      setPermDelete(null);
      await loadSummary();
      await loadList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const exportZip = async () => {
    const entity_types = Object.entries(exportTypes)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (!entity_types.length) {
      toast.warn('Select at least one entity type');
      return;
    }
    try {
      const r = await api.post('/admin/data-archive/export-zip', {
        entity_types,
        include_documents: includeDocs && entity_types.includes('document')
      });
      toast.success(r.data?.message || 'Backup created');
      await loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Export failed');
    }
  };

  const archiveDemo = async () => {
    try {
      await api.post('/admin/data-archive/demo-test-data/archive');
      toast.success('Demo/test users archived');
      await loadSummary();
      await loadList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Archive demo failed');
    }
  };

  const purgeDemo = async () => {
    try {
      await api.post('/admin/data-archive/demo-test-data/purge', {
        confirm: purgeConfirm
      });
      toast.success('Demo users purged from database');
      setPurgeOpen(false);
      setPurgeConfirm('');
      await loadSummary();
      await loadList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Purge failed');
    }
  };

  const summaryChips = useMemo(() => {
    if (!summary) return null;
    return (
      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
        {ENTITY_TABS.map(({ key, label }) => (
          <Chip key={key} label={`${label}: ${summary[key] ?? 0}`} size="small" variant="outlined" />
        ))}
        <Chip label={`Total archived: ${summary.total ?? 0}`} size="small" color="secondary" />
      </Stack>
    );
  }, [summary]);

  const rows = list?.items || [];

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Archived Data
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Archived records stay in the database (hidden from normal lists and login). Restore them here, export a zip to{' '}
        <strong>DataBackup</strong> under the server working directory, then permanently delete only when you no longer need the DB
        copy.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        To remove seeded demo users: archive them here (or use the button below), create a backup zip if you want an offline copy,
        then purge — purge requires accounts to already be archived.
      </Alert>

      {summaryChips}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Demo / test seed bundle
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Archives every ABC demo mailbox derived from active company domains on the ABC hosting company (users,
            employee profiles, contacts). Purge permanently deletes those rows after they are archived (type{' '}
            <strong>DELETE_DEMO_DATA</strong> to confirm).
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="contained" color="warning" onClick={archiveDemo}>
              Archive demo/test users
            </Button>
            <Button variant="outlined" color="error" onClick={() => setPurgeOpen(true)}>
              Purge archived demo users…
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Export archived rows to zip
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Only rows with <strong>archived_at</strong> set (Archived bucket) are included — not “Deleted” contacts. Writes{' '}
            <strong>manifest.json</strong> (and archived document files when selected) into <strong>DataBackup/*.zip</strong>. Listed under
            Data Administration → Backed Up Data.
          </Typography>
          <FormGroup row sx={{ mb: 1 }}>
            {ENTITY_TABS.map(({ key, label }) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={!!exportTypes[key]}
                    onChange={(e) => setExportTypes((s) => ({ ...s, [key]: e.target.checked }))}
                  />
                }
                label={label}
              />
            ))}
          </FormGroup>
          <FormControlLabel
            control={<Checkbox checked={includeDocs} onChange={(e) => setIncludeDocs(e.target.checked)} disabled={!exportTypes.document} />}
            label="Include document files from disk (only when Documents is checked)"
          />
          <Box sx={{ mt: 1 }}>
            <Button variant="contained" onClick={exportZip}>
              Create backup zip
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        {ENTITY_TABS.map((t) => (
          <Tab key={t.key} label={t.label} />
        ))}
      </Tabs>

      {loading ? (
        <CircularProgress />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Record</TableCell>
              <TableCell>Id</TableCell>
              <TableCell>Archived at</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color="text.secondary">No archived rows in this category.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{rowLabel(entityType, row)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.id}</TableCell>
                  <TableCell>{row.archived_at ? new Date(row.archived_at).toLocaleString() : '—'}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={() => restore(row.id)}
                      disabled={busyId === row.id}
                    >
                      Restore
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setPermDelete({ id: row.id, entityType, row })}
                      disabled={busyId === row.id}
                    >
                      Delete DB row
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={purgeOpen} onClose={() => setPurgeOpen(false)}>
        <DialogTitle>Purge archived demo users</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This permanently deletes all ABC demo accounts for every such domain (and their HR rows) from the database. They
            must already be archived.
            Create a backup zip first if you need an offline copy.
          </Typography>
          <TextField
            fullWidth
            label='Type DELETE_DEMO_DATA to confirm'
            value={purgeConfirm}
            onChange={(e) => setPurgeConfirm(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurgeOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={purgeConfirm !== 'DELETE_DEMO_DATA'} onClick={purgeDemo}>
            Purge
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!permDelete} onClose={() => setPermDelete(null)}>
        <DialogTitle>
          {permDelete?.entityType === 'document'
            ? 'Permanently delete archived document?'
            : 'Permanently delete archived row?'}
        </DialogTitle>
        <DialogContent>
          {permDelete?.entityType === 'document' ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                This will remove the document row from the database <strong>and delete the file from the documents folder</strong> on
                the server (if it still exists).
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {permDelete.row?.file_name || permDelete.row?.title
                  ? `File: ${permDelete.row.file_name || permDelete.row.title}`
                  : null}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Ensure you have a backup zip if you may need this file later.
              </Typography>
            </>
          ) : (
            <Typography variant="body2">
              This removes the row from the database. Ensure you have a backup zip if you may need it later. FK constraints may
              block deletes (e.g. accounts still referenced by jobs).
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={permanentDelete} disabled={!!busyId}>
            Approved
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
